import { FC, ReactNode, useMemo } from 'react';
import { Button, Card, Col, Divider, Row } from 'antd';
import { motion, useReducedMotion } from 'framer-motion';
import { LogIn, LucideDoorOpen } from 'lucide-react';
import { TbArrowLeft, TbUfoOff } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { MacOSIconImage } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ThemeSegmented } from '@/shared/components/segmented';
import { useUserStore } from '@/stores';
import { centerComponentInMiddleofViewWithoutAlignItems } from '@/utilities';

import AccessStateSection from '../AccessStateSection';
import PublicPageContextMenu from '../PublicPageContextMenu';
import Metadata from './meta';

interface ErrorViewProps {
    title?: string | ReactNode;
    message?: string | ReactNode;
}

interface StarConfig {
    id: number;
    startX: number;
    startY: number;
    dx: number;
    dy: number;
    delay: number;
    duration: number;
    scale: number;
}

const ErrorView: FC<ErrorViewProps> = ({ title, message }): JSX.Element => {
    const navigate = useNavigate();

    const { token } = useAntDesignTheme();

    const { selectedUser: user } = useUserStore();

    const isLoggedIn = !!user?.id;

    const ShootingStars: FC = () => {
        const { token } = useAntDesignTheme();
        const reduceMotion = useReducedMotion();

        const stars = useMemo<StarConfig[]>(() => {
            return Array.from({ length: 7 }).map((_, i) => {
                const startX = Math.random() * 100;
                const startY = Math.random() * 100;

                const angle = Math.random() * Math.PI * 2;
                const distance = 25 + Math.random() * 35;

                const dx = Math.cos(angle) * distance;
                const dy = Math.sin(angle) * distance;

                return {
                    id: i,
                    startX,
                    startY,
                    dx,
                    dy,
                    delay: Math.random() * 12,
                    duration: 1.6 + Math.random() * 1.5,
                    scale: 0.6 + Math.random() * 0.7,
                };
            });
        }, []);

        if (reduceMotion) {return null;}

        return (
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    zIndex: 0,
                    height: '100%',
                }}
            >
                {stars.map((star) => (
                    <motion.div
                        key={star.id}
                        initial={{
                            x: `${star.startX}vw`,
                            y: `${star.startY}vh`,
                            opacity: 0,
                            scale: star.scale,
                        }}
                        // animate={{
                        //     x: `${star.startX + star.dx}vw`,
                        //     y: `${star.startY + star.dy}vh`,
                        //     opacity: [0, 1, 1, 0],
                        // }}
                        animate={{
                            x: `${star.startX + star.dx}vw`,
                            y: `${star.startY + star.dy}vh`,
                            opacity: [0, 1, 1, 0],
                            boxShadow: [
                                `0 0 4px ${token.colorText}`,
                                `0 0 10px ${token.colorText}`,
                                `0 0 4px ${token.colorText}`,
                            ],
                        }}
                        transition={{
                            duration: star.duration,
                            delay: star.delay,
                            ease: 'easeOut',
                            repeat: Infinity,
                            repeatDelay: 6 + Math.random() * 10,
                        }}
                        style={{
                            position: 'absolute',
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            background: token.purple5,
                            boxShadow: `0 0 8px ${token.purple5}`,
                        }}
                    />
                ))}
            </div>
        );
    };

    // const handleReportMeteorSighting = async () => {
    //     try {
    //         await authorizedAxiosInstance.put('/api/system/ee/meteor').then((res) => {
    //             if (res?.status === 200) {
    //                 toast(
    //                     <>
    //                         <Avatar
    //                             size={'small'}
    //                             icon={<TbPlanet size='67.5%' style={{ color: token.colorTextTertiary }} />}
    //                             shape='square'
    //                             style={{
    //                                 marginRight: '0.375rem',
    //                                 border: `1px solid ${token.colorBorder}`,
    //                                 verticalAlign: '-0.1875rem',
    //                                 backgroundColor: token.colorBgContainer,
    //                             }}
    //                         />
    //                         Meteor Reported
    //                     </>,
    //                     {
    //                         description: (
    //                             <>
    //                                 <div style={{ color: token.colorTextTertiary, margin: '0.25rem 0' }}>
    //                                     Thank you for your contribution to scientific research. Please check your email
    //                                     for a small thank you from us!
    //                                 </div>
    //                                 <Row style={{ marginTop: '0.75rem' }} align={'middle'} wrap={false}>
    //                                     <Col flex='none'>
    //                                         <Button
    //                                             size='small'
    //                                             style={{ padding: '0.25rem' }}
    //                                             onClick={() => {
    //                                                 window.open(
    //                                                     `https://mail.google.com/mail?authuser=${session?.passport?.user?.emailAddress}`,
    //                                                     '_blank',
    //                                                 );
    //                                             }}
    //                                         >
    //                                             <Avatar
    //                                                 size={16}
    //                                                 shape='square'
    //                                                 style={{ border: `1px solid ${token.colorBorder}` }}
    //                                                 icon={
    //                                                     <img
    //                                                         src={GmailSVGIcon}
    //                                                         alt='Gmail'
    //                                                         style={{ ...imgLogoStyling, transform: 'scale(0.8)' }}
    //                                                     />
    //                                                 }
    //                                             />
    //                                             Open Gmail
    //                                             <TbArrowUpRight style={{ color: token.colorTextTertiary }} />
    //                                         </Button>
    //                                     </Col>
    //                                     <Col flex='none'>
    //                                         <Divider type='vertical' />
    //                                     </Col>
    //                                     <Col flex='none'>
    //                                         <Button size='small' style={{ padding: '0.25rem' }}>
    //                                             <Avatar
    //                                                 size={16}
    //                                                 shape='square'
    //                                                 style={{ border: `1px solid ${token.colorBorder}` }}
    //                                                 icon={
    //                                                     <img
    //                                                         src={MicrosoftOutlookSVGIcon}
    //                                                         alt='Gmail'
    //                                                         style={{ ...imgLogoStyling, transform: 'scale(0.8)' }}
    //                                                     />
    //                                                 }
    //                                                 onClick={() => {
    //                                                     window.open('https://outlook.office.com/mail', '_blank');
    //                                                 }}
    //                                             />
    //                                             Open Outlook
    //                                             <TbArrowUpRight style={{ color: token.colorTextTertiary }} />
    //                                         </Button>
    //                                     </Col>
    //                                 </Row>
    //                             </>
    //                         ),
    //                         dismissible: false,
    //                         position: 'bottom-center',
    //                         closeButton: true,
    //                         duration: 100000,
    //                     },
    //                 );
    //             }
    //         });
    //     } catch (error) {
    //         captureException(error);
    //     }
    // };

    return (
        <>
            <Metadata />
            <PublicPageContextMenu>
                <div
                    style={{
                        position: 'relative',
                        backgroundColor: token.colorBgContainer,
                        height: '100%',
                        width: '100%',
                        ...centerComponentInMiddleofViewWithoutAlignItems,
                        overflow: 'hidden',
                        backgroundImage: `radial-gradient(
                        circle at 0.5rem 0.5rem,
                        ${token.colorBorder} 0.125rem,
                        transparent 0
                    )`,
                        backgroundSize: '1.5rem 1.5rem',
                    }}
                >
                    <ShootingStars />
                    <Card
                        styles={{ header: { padding: '0 0.75rem' } }}
                        title={
                            <Row align={'middle'}>
                                <Col flex='none'>
                                    <a href='https://countercyclical.com' target='_blank' rel='noopener noreferrer'>
                                        <img
                                            src={MacOSIconImage}
                                            alt='Icon'
                                            height={36}
                                            width={36}
                                            style={{ marginTop: '0.25rem', cursor: 'pointer' }}
                                        />
                                    </a>
                                </Col>
                                <Col flex='auto'></Col>
                                {!isLoggedIn && (
                                    <Col flex='none'>
                                        <Button
                                            onClick={() => {
                                                navigate('/login');
                                            }}
                                        >
                                            <LogIn size='1rem' />
                                            Login
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        }
                        style={{
                            position: 'relative',
                            zIndex: 1,
                            minWidth: 320,
                            maxWidth: 480,
                            margin: '0 auto',
                            backgroundColor: token.colorBgBase,
                            borderRadius: token.borderRadiusLG * 2,
                            boxShadow: token.boxShadow,
                        }}
                        suppressContentEditableWarning
                    >
                        <AccessStateSection
                            icon={
                                !isLoggedIn ? (
                                    <LucideDoorOpen size='1.5rem' style={{ color: token.colorTextTertiary }} />
                                ) : (
                                    <TbUfoOff size='1.5rem' style={{ color: token.colorTextTertiary }} />
                                )
                            }
                            label={!isLoggedIn ? "You're Not Logged In" : 'View Not Found'}
                            description={
                                !isLoggedIn
                                    ? "You're not able to access this view because you're not logged in. Please log into Countercyclical to continue."
                                    : "Looks like you've wandered to the edge of Countercyclical. There's not a whole lot here, but feel free to stay for as long as you'd like."
                            }
                        />
                        <Divider style={{ margin: '1rem 0' }} />
                        {/* Content */}
                        <Row align='middle'>
                            <Col flex='auto' />
                            <Col flex='none'>
                                <Button
                                    icon={<TbArrowLeft size={'1rem'} style={{ verticalAlign: '-0.2rem' }} />}
                                    type='primary'
                                    onClick={() => {
                                        navigate('/');
                                    }}
                                >
                                    Back to Home
                                </Button>
                            </Col>
                            {/* <Col flex='none'>
                                <Divider type='vertical' />
                            </Col>
                            <Col flex='none'>
                                <Button
                                    icon={<TbTelescope size={'1rem'} style={{ verticalAlign: '-0.2rem' }} />}
                                    style={{ padding: '0.5rem' }}
                                    onClick={handleReportMeteorSighting}
                                >
                                    Report a Meteor Sighting
                                </Button>
                            </Col> */}
                            <Col flex='auto' />
                        </Row>
                        <Divider style={{ margin: '1.5rem 0', marginTop: '1rem' }} />
                        <div style={{ width: '100%', textAlign: 'center' }}>
                            <ThemeSegmented />
                        </div>
                    </Card>
                </div>
            </PublicPageContextMenu>
        </>
    );
};

export default ErrorView;
